import type { Stripe } from '@stripe/stripe-js';

// Stripe configuration types
export interface StripePriceIds {
  basic: {
    monthly: string;
    annual: string;
  };
  starter: {
    monthly: string;
    annual: string;
  };
  startup: {
    monthly: string;
    annual: string;
  };
  enterprise: {
    monthly: string;
    annual: string;
  };
  // Price Plan v2 — optional (Stripe self-serve for NEW cohort)
  business?: {
    monthly: string;
    annual: string;
  };
  premium?: {
    monthly: string;
    annual: string;
  };
  // Digital Staff credit plans (Core / Growth).
  ai_starter?: {
    monthly: string;
    annual: string;
  };
  ai_growth?: {
    monthly: string;
    annual: string;
  };
}

export type PlanType =
  | 'starter'
  | 'startup'
  | 'enterprise'
  | 'basic'
  | 'business'
  | 'premium'
  // Price Plan v2 — Digital Staff credit plans (Core / Growth) sold via Stripe.
  | 'ai_starter'
  | 'ai_growth';
export type BillingInterval = 'monthly' | 'annual';

// Price Plan v2 — GET /v1/billing/visible-plans/{userId}
export interface BackendPlanDescriptor {
  planCode: string;
  displayName: string;
  monthlyCents: number;
  yearlyCents: number;
  sessionLimit: number;
  isComingSoon: boolean;
  upgradeEntry: boolean;
  shopifyMonthlySellingPlan?: string | null;
  shopifyYearlySellingPlan?: string | null;
  stripeMonthlyPriceId?: string | null;
  stripeYearlyPriceId?: string | null;
}

export interface VisiblePlansResponse {
  cohort: 'LEGACY' | 'NEW';
  cohortLockedAt: string | null;
  eoyAt: string | null;
  plans: BackendPlanDescriptor[];
}

// Payment form data
export interface PaymentFormData {
  email: string;
  cardholderName: string;
  billingAddress: {
    country: string;
    address: string;
  };
}

// Plan information for payment page
export interface PaymentPlan {
  id: PlanType;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  features: string[];
  credits: number;
  isRecommended?: boolean;
}

// Payment intent response
export interface CreatePaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  customerId?: string;
  subscriptionId?: string;
}

// Setup intent creation request
export interface CreateSetupIntentRequest {
  email: string;
  priceId: string;
}

// Setup intent response
export interface CreateSetupIntentResponse {
  id: string;
  priceId: string;
  clientSecret: string;
}

// Trial creation request
export interface CreateTrialRequest {
  email: string;
  name: string;
  paymentMethodId: string;
  setupIntentId: string;
  priceId: string;
  // Price Plan v2 (T3.1 / D3): backend plan code + interval so chatbot-api
  // validates the (user, plan, interval) tuple via PlanCatalogService.
  planCode?: string;
  interval?: 'MONTHLY' | 'YEARLY';
}

// Trial creation response
export interface CreateTrialResponse {
  success: boolean;
  subscriptionId?: string;
  customerId?: string;
  trialEnd?: string;
  message?: string;
}

export interface CreatePaymentRequest {
  email: string;
  name: string;
  paymentMethodId: string;
  setupIntentId: string;
  priceId: string;
  // Price Plan v2 (T3.1 / D3): see CreateTrialRequest.
  planCode?: string;
  interval?: 'MONTHLY' | 'YEARLY';
}

export interface CreatePaymentResponse {
  success: boolean;
  subscriptionId?: string;
  customerId?: string;
  message?: string;
}

// Stripe Elements appearance configuration
export interface StripeAppearance {
  theme?: 'stripe' | 'night' | 'flat';
  variables?: {
    colorPrimary?: string;
    colorBackground?: string;
    colorText?: string;
    colorDanger?: string;
    fontFamily?: string;
    spacingUnit?: string;
    borderRadius?: string;
  };
  rules?: {
    [selector: string]: {
      [property: string]: string;
    };
  };
}

// Stripe Elements options
export interface StripeElementsOptionsBase {
  currency: string;
  appearance?: StripeAppearance;
}

// Subscription mode options
export interface StripeElementsOptionsSubscription extends StripeElementsOptionsBase {
  mode: 'subscription';
  amount?: number;
  setupFutureUsage?: 'on_session' | 'off_session';
  paymentMethodCreation?: 'manual';
}

// Payment mode options
export interface StripeElementsOptionsPayment extends StripeElementsOptionsBase {
  mode: 'payment';
  amount: number;
  clientSecret?: string;
  setupFutureUsage?: 'on_session' | 'off_session';
}

// Union type for all options
export type StripeElementsOptions =
  | StripeElementsOptionsSubscription
  | StripeElementsOptionsPayment;

// Payment error types
export interface PaymentError {
  type: 'card_error' | 'validation_error' | 'api_error' | 'network_error';
  code?: string;
  message: string;
  param?: string;
}

// Payment state for store management
export interface PaymentState {
  // Current payment data
  selectedPlan: PaymentPlan | null;
  billingInterval: BillingInterval;
  formData: Partial<PaymentFormData>;

  // Stripe objects
  stripe: Stripe | null;
  elements: any;

  // Loading and error states
  isLoading: boolean;
  isProcessing: boolean;
  error: PaymentError | null;

  // Payment process state
  paymentIntent: CreatePaymentIntentResponse | null;
  paymentConfirmed: boolean;
}

// Trial configuration
export interface TrialConfig {
  duration: number; // in days
  description: string;
}

export const DEFAULT_TRIAL_CONFIG: TrialConfig = {
  duration: 14,
  description: '14 day free trial',
};

export type PaymentNotificationEventType =
  | 'customer.subscription.created'
  | 'customer.subscription.updated'
  | 'customer.subscription.deleted'
  | 'shopify.subscription.updated'
  | 'shopify.subscription.deleted';

export interface PaymentNotification {
  id: string;
  eventType: PaymentNotificationEventType;
  userId: string;
  timestamp: number;
  data?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export const VALID_SUBSCRIPTION_EVENT_TYPES: PaymentNotificationEventType[] = [
  'customer.subscription.created',
  'customer.subscription.updated',
  'shopify.subscription.updated',
];

export const VALID_CANCEL_EVENT_TYPES: PaymentNotificationEventType[] = [
  'customer.subscription.deleted',
  'customer.subscription.updated',
  'shopify.subscription.deleted',
];

export interface PaymentNotificationListenerOptions {
  timeWindowMs?: number;

  includeMetadataChanges?: boolean;

  source?: 'default' | 'cache';
}

// Cancel plan request and response types
export interface CancelPlanRequest {
  reason: string;
}

export interface CancelPlanResponse {
  reason: string;
  comment?: string;
  errorCode?: number;
  errorMessage?: string;
}
